import {
  Button,
  FormControl,
  Grid,
  MenuItem,
  SxProps,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectLight } from "../../../../common/components/styled/styled";
import { VerticalAlignGrid } from "../../../invoices/styled";
import { ReactComponent as EditIcon } from "../../../../common/icons/editGray.svg";
import { ReactComponent as ViewIcon } from "../../../../common/icons/viewGray.svg";
import {
  PricingCategorySelection,
  PRICING_SPEC_DEFAULT_OPTIONS,
} from "../constants";
import { NumericFormat } from "react-number-format";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import InfoTooltip from "../../../../common/components/functional/InfoTooltip/InfoTooltip";

interface Props {
  label: string;
  isFieldEnabled?: boolean;
  labelColor?: string;
  labelTooltip?: string;
  inputWidth?: number;
  selectOptions?: string[];
  defaultSelection?: string;
  selectWidth?: number;
  inputValue: string;
  inputRequired?: boolean;
  onInputChange: (newInput: string) => void;
  inputUnitWidth?: number;
  onSelectionChange: (newSelection: string) => void;
  showButton?: boolean;
  showViewButton?: boolean;
  editButtonText?: string | null;
  viewButtonText?: string | null;
  onButtonClick?: () => void;
  selectSx?: SxProps;
  disabled?: boolean;
  showOnlyForHourlyBillingLabel?: boolean;
}

const DEFAULT_INPUT_WIDTH = 2;
const DEFAULT_INPUT_UNIT_WIDTH = 0.5;
const DEFAULT_SELECT_WIDTH = 2;

const Input = ({ ...props }) => (
  <CommonTextField
    {...props}
    inputProps={{
      style: {
        textAlign: "center",
      },
    }}
  />
);

const LabelInputSelectRow = ({
  label,
  labelColor,
  labelTooltip,
  inputWidth = DEFAULT_INPUT_WIDTH,
  selectOptions = PRICING_SPEC_DEFAULT_OPTIONS,
  defaultSelection,
  selectWidth = DEFAULT_SELECT_WIDTH,
  inputValue,
  inputRequired,
  onInputChange,
  inputUnitWidth = DEFAULT_INPUT_UNIT_WIDTH,
  onSelectionChange,
  showButton,
  showViewButton = false,
  editButtonText,
  viewButtonText,
  onButtonClick,
  selectSx,
  disabled,
  showOnlyForHourlyBillingLabel = false,
}: Props) => {
  const { t } = useTranslation("customers");

  const [selection, setSelection] = useState<string>(
    defaultSelection || selectOptions[0],
  );

  const handleSelectionChange = (newSelection: string) => {
    setSelection(newSelection);
    onSelectionChange(newSelection);
  };

  const showHourlyBillingLabel = (item: string) =>
    showOnlyForHourlyBillingLabel &&
    (item === PricingCategorySelection.FixedPrice ||
      item === PricingCategorySelection.Override);
  const notBilledIsSelected = selection === PricingCategorySelection.NotBilled;
  const fixedPriceIsSelected =
    selection === PricingCategorySelection.FixedPrice;
  const inputIsDisabled = notBilledIsSelected;

  return (
    <VerticalAlignGrid container spacing={1}>
      <Grid
        container
        item
        xs={3}
        justifyContent="flex-end"
        paddingRight="0.5rem"
        textAlign="end"
      >
        <Typography variant="secondary" color={labelColor}>
          {label}
        </Typography>
        {labelTooltip && (
          <Grid item sx={{ marginLeft: "0.5rem" }}>
            <InfoTooltip
              title={labelTooltip}
              slotProps={{ popper: { sx: { whiteSpace: "pre-line" } } }}
            />
          </Grid>
        )}
      </Grid>
      <Grid
        item
        xs={
          fixedPriceIsSelected && !inputIsDisabled
            ? inputWidth - inputUnitWidth
            : inputWidth
        }
      >
        <FormControl fullWidth size="small">
          <NumericFormat
            disabled={inputIsDisabled || disabled}
            value={inputIsDisabled ? "" : inputValue}
            error={!inputIsDisabled && inputRequired && !inputValue}
            decimalSeparator=","
            valueIsNumericString
            onValueChange={(values) => onInputChange(values.value)}
            customInput={Input}
            decimalScale={fixedPriceIsSelected ? 2 : 3}
          />
        </FormControl>
      </Grid>
      {fixedPriceIsSelected && !inputIsDisabled && (
        <Grid item xs={inputUnitWidth}>
          {t("invoices:euroUnit")}
        </Grid>
      )}
      <Grid item xs={selectWidth}>
        <FormControl fullWidth size="small">
          <SelectLight
            labelId="label"
            onChange={(event) =>
              handleSelectionChange(event.target.value as string)
            }
            value={selection}
            disabled={disabled}
            sx={selectSx}
          >
            {selectOptions.map((item) => (
              <MenuItem key={item} value={item} dense>
                {!showHourlyBillingLabel(item)
                  ? t(`contract.${item}`)
                  : t(`contract.${item}-hourly`)}
              </MenuItem>
            ))}
          </SelectLight>
        </FormControl>
      </Grid>
      {showButton && (
        <Grid item>
          <Button
            variant="text"
            startIcon={showViewButton ? <ViewIcon /> : <EditIcon />}
            onClick={onButtonClick}
          >
            <Typography color="textSecondary" fontSize="14px">
              {showViewButton ? viewButtonText : editButtonText}
            </Typography>
          </Button>
        </Grid>
      )}
    </VerticalAlignGrid>
  );
};

export default LabelInputSelectRow;
